import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { LoadingOverlay } from '@finpay-development/shared-components';
import { useDispatch } from "react-redux";
import { AppDispatch } from '../../shared/state/store';
import { getv2Reports } from '../state/users/admin-thunk';
import {configSettings} from '../../shared/configuration/config-settings';

interface QuickSightDashboardProps {
  reportId: number;
  clientId: number;
  startDate: string;
  endDate: string;
}

function QuickSightDashboard(props: QuickSightDashboardProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { reportId, clientId, startDate, endDate } = props;
  const dashboardRef = useRef<HTMLDivElement | null>(null);
  const [embeddedDashboard, setEmbeddedDashboard] = useState<any | null>(null);
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [dashboardUrl, setDashboardUrl] = useState<string | null>(null);
  const [embeddingContext, setEmbeddingContext] = useState<any | null>(null);
  const [isLoadingDash, setIsLoadingDash] = useState<boolean>(false);
  const hasFetched = useRef(false); // Tracks if the fetch has already been triggered

  useEffect(() => {
    if (!hasFetched.current && reportId !== null && clientId !== null) {
      hasFetched.current = true;
      fetchDashboardEmbed(clientId);
      setIsLoadingDash(true)
    }
  }, [clientId, reportId]);

    const fetchDashboardEmbed = async (clientId: number) => {
      try {

        // Call new get Endpoint
        const response = await dispatch(getv2Reports(reportId));
        const embedUrl = new URL(response.payload.embedUrl);

        // Determine baseURL from environment
        const appEnv = configSettings.app_environment;
        const baseURL = `https://report.${appEnv}.finpay.net/`;

        embedUrl.searchParams.set('clientId', clientId.toString());
        embedUrl.searchParams.set('startDate', startDate);
        embedUrl.searchParams.set('endDate', endDate);
        embedUrl.searchParams.set('baseURL', baseURL);
        
        setDashboardUrl(embedUrl.toString());
        hasFetched.current = false;
        setIsLoadingDash(false)
      } catch (error) {
        console.error('Error fetching dashboard URL:', error);
      }
    };

  const createContext = async () => {
    const context = await createEmbeddingContext();
    setEmbeddingContext(context);
  };

  useEffect(() => {
    if (dashboardUrl) {
      createContext();
    }
  }, [dashboardUrl]);

  useEffect(() => {
    if (embeddingContext && dashboardUrl) {
      embedDashboard();
    }
  }, [dashboardUrl, embeddingContext]);

  const embedDashboard = async () => {
    const options = {
      url: dashboardUrl,
      container: dashboardRef.current,
      height: "680px",
      width: "100%",
    };
    try {
      const newEmbeddedDashboard = await embeddingContext.embedDashboard(options);
      setEmbeddedDashboard(newEmbeddedDashboard); // Store embedded dashboard for later interaction
    } catch (error) {
      console.error('Error embedding dashboard:', error);
    }
  };

  useEffect(() => {
    if (embeddedDashboard && dashboardId && dashboardId.length > 0) {
      embeddedDashboard.navigateToDashboard(dashboardId, {});
    }
  }, [dashboardId, embeddedDashboard]);

  return (
    isLoadingDash ? (
      <LoadingOverlay />
    ) : (
    <>
      <main style={{ display: "flex", flexDirection: "column" }}>
        <div ref={dashboardRef} style={{ overflow: 'hidden' }} />
      </main>
    </>
    )
  );
}

export default QuickSightDashboard;
